import { create } from "zustand";

import type { EnemyType } from "../../api/types";

/** Dossier d'où vient la demande (champ « Ennemi » des Données initiales) : une fois l'ENI validé, la vue propose
 *  « Employer dans le dossier ». */
export interface CustomEnemyOrigin {
  exerciseId: string | null;
  exerciseName: string | null;
}

/** Choix à reporter dans le brouillon des Données initiales (rien n'est enregistré d'office). */
export interface CustomEnemyUse {
  exerciseId: string | null;
  enemyId: string;
  /** Ennemi de référence porteur, pour le champ `enemy_type` du brouillon. */
  baseEnemy: EnemyType;
  at: number;
}

/** ENI à montrer à l'ouverture de la vue « ENI personnalisés » (null : le premier de la liste), création demandée et
 *  dossier d'origine ; posé par le champ « Ennemi » des Données initiales, lu par la vue à son ouverture. `use` :
 *  choix « Employer dans le dossier », relu par les Données initiales. */
interface CustomEnemyTarget {
  enemyId: string | null;
  create: boolean;
  origin: CustomEnemyOrigin | null;
  use: CustomEnemyUse | null;
  at: number;
  open: (enemyId: string | null, options?: { create?: boolean; origin?: CustomEnemyOrigin | null }) => void;
  employ: (use: Omit<CustomEnemyUse, "at">) => void;
  consumeUse: () => void;
}

export const useCustomEnemyTarget = create<CustomEnemyTarget>((set) => ({
  enemyId: null,
  create: false,
  origin: null,
  use: null,
  at: 0,
  open: (enemyId, options = {}) =>
    set({ enemyId, create: options.create ?? false, origin: options.origin ?? null, at: Date.now() }),
  employ: (use) => set({ use: { ...use, at: Date.now() } }),
  consumeUse: () => set({ use: null }),
}));

/** Valeur d'une option « ENI personnalisé » du champ « Ennemi » (le serveur accepte aussi « custom:<id> »). */
export const CUSTOM_PREFIX = "custom:";
export const customValue = (id: string) => `${CUSTOM_PREFIX}${id}`;
export const customIdOf = (value: string): string | null => (value.startsWith(CUSTOM_PREFIX) ? value.slice(CUSTOM_PREFIX.length) : null);
